import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Lock, Download, Trash2, ShieldCheck, Fingerprint, Loader2, Eye, Images } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function GalleryImage({ id, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let u; api.get(`/island/gallery/${id}/view`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {}); return () => u && URL.revokeObjectURL(u); }, [id]);
  return url ? <img src={url} alt="" className={className} /> : <div className={`animate-pulse bg-secondary ${className}`} />;
}

function AccessDialog({ item, frigate, onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState(item?.allowed || []);
  const [traces, setTraces] = useState([]);
  useEffect(() => { if (item) { api.get("/members", { params: { frigate } }).then((r) => setMembers(r.data.members)); api.get(`/island/gallery/${item.id}/traces`).then((r) => setTraces(r.data.traces)).catch(() => {}); setSel(item.allowed || []); } }, [item, frigate]);
  const save = async () => { try { await api.put(`/island/gallery/${item.id}/access`, { allowed: sel }); toast.success("Download clearance updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg" data-testid="access-dialog">
        <DialogHeader><DialogTitle className="font-display">Confidential clearance — {item?.title}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Officers can always download. Tick brothers who may download this picture. Every download is invisibly watermarked with the downloader's identity.</p>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {members.map((m) => (
            <label key={m.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-secondary" data-testid={`clear-${m.id}`}>
              <Checkbox checked={sel.includes(m.id)} onCheckedChange={() => setSel((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])} />
              <span className="text-sm">{m.name}</span><span className="ml-auto text-xs text-muted-foreground">{m.position}</span>
            </label>
          ))}
        </div>
        {traces.length > 0 && (
          <div><p className="label-eyebrow mb-1 text-[0.6rem]">Download log ({traces.length})</p>
            <div className="max-h-28 space-y-1 overflow-y-auto text-xs" data-testid="trace-log">{traces.map((t) => <p key={t.trace_id}><span className="font-mono-data text-[hsl(var(--gold))]">{t.trace_id}</span> · {t.name} · {new Date(t.at).toLocaleString()}</p>)}</div></div>
        )}
        <DialogFooter><Button onClick={save} className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" data-testid="access-save-btn">Save clearance</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TraceTool() {
  const ref = useRef(); const [busy, setBusy] = useState(false); const [res, setRes] = useState(null);
  const run = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    setBusy(true); setRes(null);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/island/gallery/trace", fd); setRes(data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card className="gold-ring glass p-4" data-testid="trace-tool">
      <div className="flex flex-wrap items-center gap-3">
        <Fingerprint className="h-6 w-6 text-[hsl(var(--gold))]" />
        <div className="flex-1"><p className="text-sm font-700">Leak tracer</p><p className="text-xs text-muted-foreground">Upload a leaked picture to reveal which brother downloaded it.</p></div>
        <input ref={ref} type="file" accept="image/*" hidden onChange={run} data-testid="trace-input" />
        <Button variant="outline" size="sm" className="gold-ring" onClick={() => ref.current?.click()} disabled={busy} data-testid="trace-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Trace image</Button>
      </div>
      {res && (
        <div className={`mt-3 rounded-lg p-3 text-sm ${res.found ? "bg-emerald-500/10" : "bg-destructive/10"}`} data-testid="trace-result">
          {res.found ? <>Watermark found — <b>{res.trace?.name}</b> ({res.trace?.email}) downloaded "{res.trace?.title}" on {new Date(res.trace?.at).toLocaleString()} · trace <span className="font-mono-data">{res.trace_id}</span></> : res.detail}
        </div>
      )}
    </Card>
  );
}

export default function Gallery({ frigate }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [canUpload, setCanUpload] = useState(false);
  const [view, setView] = useState(null);
  const [access, setAccess] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [confidential, setConfidential] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/island/gallery", { params: { frigate } }).then((r) => { setItems(r.data.items); setCanUpload(r.data.can_upload); });
  useEffect(() => { load(); }, [frigate]); // eslint-disable-line

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); fd.append("frigate", frigate); fd.append("title", title || f.name); fd.append("confidential", confidential ? "true" : "false"); await api.post("/island/gallery", fd); toast.success(confidential ? "Confidential picture added" : "Picture added"); setTitle(""); setConfidential(false); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };
  const del = async (it) => { if (!window.confirm(`Remove "${it.title}"?`)) return; await api.delete(`/island/gallery/${it.id}`); load(); };
  const download = (it) => downloadBlob(`/island/gallery/${it.id}/download`, `${it.title}.png`).then(() => toast.success("Downloaded — watermarked to you")).catch(() => toast.error("Not cleared to download"));

  return (
    <div data-testid="gallery">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="font-display text-lg font-700">{frigate} Gallery</h3><p className="text-xs text-muted-foreground">Frigate pictures. Confidential ones show a visible mark when viewed and carry an invisible tracking watermark when downloaded.</p></div>
        {canUpload && (
          <div className="flex flex-wrap items-center gap-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="h-9 w-44" data-testid="gallery-title-input" />
            <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={confidential} onCheckedChange={setConfidential} data-testid="gallery-confidential-checkbox" /><Lock className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />Confidential</label>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={upload} data-testid="gallery-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[hsl(var(--water))] text-white" data-testid="gallery-upload-btn">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Add picture</Button>
          </div>
        )}
      </div>
      {canUpload && <div className="mb-4"><TraceTool /></div>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((it) => (
          <Card key={it.id} className="gold-ring glass group overflow-hidden" data-testid={`gallery-item-${it.id}`}>
            <div className="relative aspect-square cursor-pointer" onClick={() => setView(it)}>
              <GalleryImage id={it.id} className="h-full w-full object-cover" />
              {it.confidential && <Badge className="absolute left-2 top-2 bg-[hsl(var(--gold))] text-[0.6rem] text-[hsl(var(--primary-foreground))]" data-testid={`confidential-${it.id}`}><Lock className="mr-1 h-3 w-3" />Confidential</Badge>}
            </div>
            <div className="p-3">
              <p className="truncate text-sm font-600">{it.title}</p>
              <p className="text-[0.65rem] text-muted-foreground">{it.uploader_name} · {it.downloads} downloads</p>
              <div className="mt-2 flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setView(it)} data-testid={`view-${it.id}`}><Eye className="h-4 w-4" /></Button>
                {it.can_download ? <Button size="sm" variant="ghost" onClick={() => download(it)} data-testid={`download-${it.id}`}><Download className="h-4 w-4 text-[hsl(var(--water))]" /></Button>
                  : <Button size="sm" variant="ghost" disabled title="Not cleared" data-testid={`locked-${it.id}`}><Lock className="h-4 w-4" /></Button>}
                {it.can_manage && it.confidential && <Button size="sm" variant="ghost" onClick={() => setAccess(it)} data-testid={`access-${it.id}`}><ShieldCheck className="h-4 w-4 text-[hsl(var(--gold))]" /></Button>}
                {it.can_manage && <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => del(it)} data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {items.length === 0 && <Card className="gold-ring glass p-8 text-center text-sm text-muted-foreground"><Images className="mx-auto mb-2 h-8 w-8 opacity-40" />No pictures in the {frigate} gallery yet.</Card>}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl bg-black p-0" data-testid="gallery-viewer">
          <DialogHeader><DialogTitle className="sr-only">{view?.title}</DialogTitle></DialogHeader>
          {view && <div><GalleryImage id={view.id} className="max-h-[75vh] w-full object-contain" /><div className="flex items-center justify-between p-3 text-white"><p className="text-sm">{view.title}{view.confidential && <Badge className="ml-2 bg-[hsl(var(--gold))] text-[0.6rem] text-[hsl(var(--primary-foreground))]">Confidential</Badge>}</p>{view.can_download && <Button size="sm" variant="secondary" onClick={() => download(view)}><Download className="mr-1.5 h-4 w-4" />Download</Button>}</div></div>}
        </DialogContent>
      </Dialog>
      <AccessDialog item={access} frigate={frigate} onClose={() => setAccess(null)} onSaved={load} />
    </div>
  );
}
